/**
 * React Query hooks for IA feature
 * Manages API calls and state for AI-powered appreciation generation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { iaApi } from '../api/ia.api'
import { useAppStore } from '@/app/store'
import type {
  GenerationRequest,
  SaveAppreciationRequest,
  CreatePromptRequest,
  ApproveGenerationRequest
} from '../types/ia.types'

/**
 * Query keys for React Query
 */
export const IA_QUERY_KEYS = {
  appreciation: (affaireId: number) => ['ia', 'appreciation', affaireId] as const,
  activeAffaires: ['ia', 'activeAffaires'] as const,
  recentAffairesWithoutAppreciation: ['ia', 'recentAffairesWithoutAppreciation'] as const,
  prompts: ['ia', 'prompts'] as const,
  activePrompt: ['ia', 'activePrompt'] as const,
  history: (affaireId: number) => ['ia', 'history', affaireId] as const,
  pendingApprovals: ['ia', 'pendingApprovals'] as const
}

/**
 * Hook to fetch appreciation for a specific affaire
 */
export function useAppreciation(affaireId: number | null) {
  return useQuery({
    queryKey: affaireId ? IA_QUERY_KEYS.appreciation(affaireId) : ['ia', 'appreciation', 'null'],
    queryFn: async () => {
      if (!affaireId) return null
      const response = await iaApi.getAppreciation(affaireId)
      return response.data
    },
    enabled: !!affaireId,
    staleTime: 1000 * 60 * 5 // 5 minutes
  })
}

/**
 * Hook to fetch active affaires
 */
export function useActiveAffaires() {
  return useQuery({
    queryKey: IA_QUERY_KEYS.activeAffaires,
    queryFn: async () => {
      const response = await iaApi.getActiveAffaires()
      return response.data?.affaires || []
    },
    staleTime: 1000 * 60 * 2 // 2 minutes
  })
}

/**
 * Hook to fetch 100 most recent affaires without appreciation
 */
export function useRecentAffairesWithoutAppreciation() {
  return useQuery({
    queryKey: IA_QUERY_KEYS.recentAffairesWithoutAppreciation,
    queryFn: async () => {
      const response = await iaApi.getRecentAffairesWithoutAppreciation()
      return response.data?.affaires || []
    },
    staleTime: 1000 * 60 * 2 // 2 minutes
  })
}

/**
 * Hook to search affaires manually
 */
export function useSearchAffaire(query: string | null, enabled: boolean = false) {
  return useQuery({
    queryKey: ['ia', 'searchAffaire', query] as const,
    queryFn: async () => {
      if (!query || query.trim().length === 0) return null
      const response = await iaApi.searchAffaire(query)
      return response.data
    },
    enabled: enabled && !!query && query.trim().length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false // Don't retry on 404
  })
}

/**
 * Hook to fetch all prompts
 */
export function usePrompts() {
  return useQuery({
    queryKey: IA_QUERY_KEYS.prompts,
    queryFn: async () => {
      const response = await iaApi.getPrompts()
      return response.data?.prompts || []
    },
    staleTime: 1000 * 60 * 10 // 10 minutes
  })
}

/**
 * Hook to fetch active prompt
 */
export function useActivePrompt() {
  return useQuery({
    queryKey: IA_QUERY_KEYS.activePrompt,
    queryFn: async () => {
      const response = await iaApi.getActivePrompt()
      return response.data?.prompt || null
    },
    staleTime: 1000 * 60 * 10 // 10 minutes
  })
}

/**
 * Hook to fetch generation history
 */
export function useHistory(affaireId: number | null) {
  return useQuery({
    queryKey: affaireId ? IA_QUERY_KEYS.history(affaireId) : ['ia', 'history', 'null'],
    queryFn: async () => {
      if (!affaireId) return []
      const response = await iaApi.getHistory(affaireId)
      return response.data?.history || []
    },
    enabled: !!affaireId,
    staleTime: 1000 * 60 * 5 // 5 minutes
  })
}

/**
 * Hook to generate appreciation
 */
export function useGenerateAppreciation() {
  const queryClient = useQueryClient()
  const addNotification = useAppStore(state => state.addNotification)

  return useMutation({
    mutationFn: async (request: GenerationRequest) => {
      const response = await iaApi.generateAppreciation(request)
      return response.data
    },
    onSuccess: (data, variables) => {
      // Invalidate history to show new generation
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.history(variables.affaireId) })

      // Invalidate recent affaires list to remove this affaire (now has pending approval)
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.recentAffairesWithoutAppreciation })

      // Invalidate pending approvals to show the new approval
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.pendingApprovals })

      addNotification({
        type: 'success',
        title: 'Succès',
        message: 'Appréciation générée avec succès'
      })
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.message || 'Erreur lors de la génération de l\'appréciation'
      })
    }
  })
}

/**
 * Hook to save appreciation
 */
export function useSaveAppreciation() {
  const queryClient = useQueryClient()
  const addNotification = useAppStore(state => state.addNotification)

  return useMutation({
    mutationFn: async (request: SaveAppreciationRequest) => {
      await iaApi.saveAppreciation(request)
    },
    onSuccess: (_, variables) => {
      // Invalidate appreciation query to reflect saved changes
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.appreciation(variables.affaireId) })
      // Also invalidate active affaires since this affaire now has an appreciation
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.activeAffaires })

      addNotification({
        type: 'success',
        title: 'Succès',
        message: 'Appréciation sauvegardée avec succès'
      })
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.message || 'Erreur lors de la sauvegarde de l\'appréciation'
      })
    }
  })
}

/**
 * Hook to create a new prompt
 */
export function useCreatePrompt() {
  const queryClient = useQueryClient()
  const addNotification = useAppStore(state => state.addNotification)

  return useMutation({
    mutationFn: async (request: CreatePromptRequest) => {
      await iaApi.createPrompt(request)
    },
    onSuccess: () => {
      // Invalidate prompts queries to reflect new prompt
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.prompts })
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.activePrompt })

      addNotification({
        type: 'success',
        title: 'Succès',
        message: 'Nouveau prompt créé et activé avec succès'
      })
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.message || 'Erreur lors de la création du prompt'
      })
    }
  })
}

/**
 * Hook to activate a prompt
 */
export function useActivatePrompt() {
  const queryClient = useQueryClient()
  const addNotification = useAppStore(state => state.addNotification)

  return useMutation({
    mutationFn: async (promptId: string) => {
      await iaApi.activatePrompt(promptId)
    },
    onSuccess: () => {
      // Invalidate prompts queries to reflect activated prompt
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.prompts })
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.activePrompt })

      addNotification({
        type: 'success',
        title: 'Succès',
        message: 'Prompt activé avec succès'
      })
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.message || 'Erreur lors de l\'activation du prompt'
      })
    }
  })
}

/**
 * Hook to delete a prompt
 */
export function useDeletePrompt() {
  const queryClient = useQueryClient()
  const addNotification = useAppStore(state => state.addNotification)

  return useMutation({
    mutationFn: async (promptId: string) => {
      await iaApi.deletePrompt(promptId)
    },
    onSuccess: () => {
      // Invalidate prompts queries to reflect deletion
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.prompts })

      addNotification({
        type: 'success',
        title: 'Succès',
        message: 'Prompt supprimé avec succès'
      })
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.message || 'Erreur lors de la suppression du prompt'
      })
    }
  })
}

/**
 * Hook to fetch pending approvals
 */
export function usePendingApprovals() {
  return useQuery({
    queryKey: IA_QUERY_KEYS.pendingApprovals,
    queryFn: async () => {
      const response = await iaApi.getPendingApprovals()
      return response.data?.pendingApprovals || []
    },
    staleTime: 1000 * 60 * 2 // 2 minutes
  })
}

/**
 * Hook to approve a generation
 */
export function useApproveGeneration() {
  const queryClient = useQueryClient()
  const addNotification = useAppStore(state => state.addNotification)

  return useMutation({
    mutationFn: async ({ historyId, request }: { historyId: string; request: ApproveGenerationRequest }) => {
      await iaApi.approveGeneration(historyId, request)
    },
    onSuccess: () => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.pendingApprovals })
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.recentAffairesWithoutAppreciation })
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.activeAffaires })

      addNotification({
        type: 'success',
        title: 'Succès',
        message: 'Génération approuvée et sauvegardée avec succès'
      })
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.message || 'Erreur lors de l\'approbation de la génération'
      })
    }
  })
}

/**
 * Hook to reject a generation
 */
export function useRejectGeneration() {
  const queryClient = useQueryClient()
  const addNotification = useAppStore(state => state.addNotification)

  return useMutation({
    mutationFn: async (historyId: string) => {
      await iaApi.rejectGeneration(historyId)
    },
    onSuccess: () => {
      // Invalidate pending approvals query
      queryClient.invalidateQueries({ queryKey: IA_QUERY_KEYS.pendingApprovals })

      addNotification({
        type: 'success',
        title: 'Succès',
        message: 'Génération rejetée avec succès'
      })
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.message || 'Erreur lors du rejet de la génération'
      })
    }
  })
}
